import { useState, type FormEvent } from 'react'
import { lerValorTroco, sugerirNotas, trocoInsuficiente, valorDoTroco } from '../lib/troco'
import { Box, Button, Chip, Dialog, DialogContent, InputAdornment, Stack, TextField, Typography, } from '@mui/material'
import { formatarBRL } from '../lib/formato'
import MoneyOffIcon from '@mui/icons-material/MoneyOff'
import CheckIcon from '@mui/icons-material/Check'

export function DialogTroco({
  aberto,
  total,
  trocoInicial,
  onConfirmar,
  onOutraForma,
  onFechar,
}: {
  aberto: boolean
  total: number
  trocoInicial: string
  onConfirmar: (troco: string) => void
  onOutraForma: () => void
  onFechar: () => void
}): JSX.Element {
  const [precisaTroco, setPrecisaTroco] = useState(() => !!trocoInicial.trim())
  const [texto, setTexto] = useState(trocoInicial)
  const [saiuDoCampo, setSaiuDoCampo] = useState(false)
  const valor = lerValorTroco(texto)
  const insuficiente = trocoInsuficiente(valor, total)
  const valido = valor !== null && !insuficiente

  function confirmarValor(e: FormEvent): void {
    e.preventDefault()

    if (valido) onConfirmar(texto.trim())
  }

  function ajuda(): string {
    if (insuficiente) return `Precisa ser ${formatarBRL(total)} ou mais`

    if (valor === null) return 'Digite com quanto vai pagar ou toque num valor.'

    const volta = valorDoTroco(valor, total)
    return volta === 0 ? 'Valor exato, sem troco.' : `Você recebe ${formatarBRL(volta)} de volta.`
  }

  return (
    <Dialog open={aberto} onClose={onFechar} fullWidth maxWidth="xs">
      <DialogContent sx={{ p: 3 }}>
        <Stack spacing={2}>
          <Stack direction="column" spacing={1} sx={{ justifyContent: 'center', alignItems: 'center' }}>
            <Typography variant="h4">
              Vai precisar de troco?
            </Typography>

            <Typography variant="h6" color="text.secondary">
              Total do pedido:{' '}

              <Box component="strong" sx={{ color: 'text.primary' }}>
                {formatarBRL(total)}
              </Box>
            </Typography>
          </Stack>

          {!precisaTroco ? (
            <Stack spacing={1.5}>
              <Stack direction="row" spacing={1.5}>
                <Button variant="outlined" size="large" fullWidth onClick={() => onConfirmar('')}>
                  Não
                </Button>

                <Button variant="contained" size="large" fullWidth disableElevation onClick={() => setPrecisaTroco(true)}>
                  Sim
                </Button>
              </Stack>

              <Button
                variant="text"
                size="small"
                onClick={onOutraForma}
                sx={{ alignSelf: 'center', textTransform: 'none' }}
              >
                Trocar forma de pagamento
              </Button>
            </Stack>
          ) : (
            <Stack component="form" spacing={3} onSubmit={confirmarValor} noValidate>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {sugerirNotas(total).map((nota) => (
                  <Chip
                    key={nota}
                    label={formatarBRL(nota)}
                    onClick={() => setTexto(String(nota))}
                    variant="outlined"
                    sx={{ height: 40 }}
                  />
                ))}
              </Stack>

              <TextField
                label="Troco para quanto?"
                value={texto}
                onChange={(e) => setTexto(e.target.value.replace(/[^\d.,]/g, ''))}
                onBlur={() => setSaiuDoCampo(true)}
                autoFocus
                fullWidth
                error={insuficiente && saiuDoCampo}
                helperText={ajuda()}
                slotProps={{
                  input: { startAdornment: <InputAdornment position="start">R$</InputAdornment> },
                  htmlInput: { inputMode: 'decimal', enterKeyHint: 'done' },
                }}
              />

              <Stack direction="row" spacing={1} justifyContent="center">
                <Button
                  onClick={() => onConfirmar('')}
                  fullWidth
                  variant="outlined"
                  startIcon={<MoneyOffIcon />}
                  sx={{ textTransform: 'none' }}
                >
                  Sem troco
                </Button>

                <Button
                  type="submit"
                  variant="contained"
                  disabled={!valido}
                  fullWidth
                  startIcon={<CheckIcon />}
                  sx={{ textTransform: 'none' }}
                >
                  Confirmar
                </Button>
              </Stack>
            </Stack>
          )}
        </Stack>
      </DialogContent>
    </Dialog>
  )
}
